import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import {
  Sun,
  LayoutDashboard,
  Users,
  FileText,
  Kanban,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  X,
  PlusCircle,
  UserCheck,
  BarChart3,
  Heart,
  Building,
  Calculator,
  Crown,
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, canManageUsers, canViewReports, isPlatformOwner } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    // Platform Owner only
    ...(isPlatformOwner() ? [{ path: '/platform', icon: Crown, label: 'Platform', roles: ['Admin'], isPlatformOnly: true }] : []),
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Admin', 'SalesRep', 'BackOffice'] },
    { path: '/leads', icon: Users, label: 'Leads', roles: ['Admin', 'SalesRep', 'BackOffice'] },
    { path: '/pipeline', icon: Kanban, label: 'Pipeline', roles: ['Admin', 'SalesRep', 'BackOffice'] },
    { path: '/payouts', icon: DollarSign, label: 'Payouts', roles: ['Admin', 'SalesRep', 'BackOffice'] },
    { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['Admin', 'BackOffice'] },
    { path: '/commission-structure', icon: Calculator, label: 'Commission', roles: ['Admin'] },
    { path: '/vendors', icon: Building, label: 'Vendors', roles: ['Admin'] },
    { path: '/users', icon: UserCheck, label: 'Users', roles: ['Admin'] },
    { path: '/nonprofits', icon: Heart, label: 'Nonprofits', roles: ['Admin', 'BackOffice'] },
    { path: '/settings', icon: Settings, label: 'Settings', roles: ['Admin', 'SalesRep', 'BackOffice'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Solar Moguls" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-heading font-bold text-lg text-slate-900 dark:text-white">
              Solar Moguls
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Quick actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800">
          <Link to="/leads/new">
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20"
              data-testid="new-lead-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              New Lead
            </Button>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="h-16 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Page title - hidden on mobile */}
            <div className="hidden lg:block" />

            {/* User menu */}
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 font-medium">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {user?.name}
                      </p>
                      <Badge variant="secondary" className="text-xs">
                        {user?.role}
                      </Badge>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.name}</span>
                      <span className="text-xs font-normal text-slate-500">
                        {user?.email}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
